'use client';

import type { CryptoRelatedNewsItem } from '@/hooks/use-crypto-related-news';
import { formatDateTime } from '@/lib/utils/date';
import { useTranslation } from '@/lib/i18n';
import { groupBy } from 'lodash';
import Image from 'next/image';
import NewsCard from '../cards/NewsCard';
import type { MarketEventNewsItem } from './MarketEvent';

export interface NewsTimelineItem {
  newsId: string;
  newsTitle: string;
  source: string;
  sourceIconUrl: string;
  sourceUrl?: string;
  publishTime: string | Date;
  importance?: number;
  questions?: string[];
}

export interface NewsTimelineDayGroup {
  date: string;
  news: NewsTimelineItem[];
}

export interface NewsTimelineMonthGroup {
  month: string;
  days: NewsTimelineDayGroup[];
}

export type NewsTimelineSourceItem = MarketEventNewsItem | CryptoRelatedNewsItem;

export function createNewsTimelineGroups(news: NewsTimelineSourceItem[] = []) {
  const groupedByMonth = groupBy(news, (item) => formatDateTime(item.publish_time, 'MMM'));

  return Object.entries(groupedByMonth).map(([month, monthNews]) => ({
    month,
    days: Object.entries(
      groupBy(monthNews, (item) => formatDateTime(item.publish_time, 'MM-DD')),
    ).map(([date, dayNews]) => ({
      date,
      news: dayNews.map(toNewsTimelineItem),
    })),
  }));
}

function toNewsTimelineItem(item: NewsTimelineSourceItem): NewsTimelineItem {
  return {
    newsId: item.news_id,
    newsTitle: 'news_title' in item ? item.news_title : item.title,
    source: item.source,
    sourceIconUrl: item.source_icon_url,
    sourceUrl: item.source_url,
    publishTime: item.publish_time,
    importance: item.importance_level,
    questions: item.recommended_questions,
  };
}

export default function NewsTimeline({
  groups,
  hasMore,
  isLoadingMore,
  setSentinelRef,
}: {
  groups: NewsTimelineMonthGroup[];
  /** 是否还有下一页（配合 setSentinelRef 启用无限滚动） */
  hasMore?: boolean;
  /** 下一页加载中 */
  isLoadingMore?: boolean;
  /** 分页 hook 的 sentinel ref（如 useEntityNews 的 setSentinelRef），进入视口自动加载下一页 */
  setSentinelRef?: (el: Element | null) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex">
      <div className="border-r-2 border-[#2B2B2B] pl-6"></div>
      <div>
        {groups.map(({ month, days }) => (
          <div key={month}>
            <div className="h-10 sticky -top-4">
              <span className="absolute bg-[#A772FF] text-white text-xs h-4.5 rounded-lg px-3 leading-4.5 -translate-x-1/2">
                {month}
              </span>
            </div>
            {days.map(({ date, news: dayNews }) => (
              <div key={date} className="pl-5 mb-4">
                <div className="flex items-center gap-3">
                  <Image
                    src="/image/timeline-arrow.png"
                    width={43}
                    height={9}
                    alt=""
                    className="-ml-6.5"
                  />
                  <div className="text-xs font-semibold text-white py-2.5">{date}</div>
                </div>
                <div className="space-y-6 w-[320px]">
                  {dayNews.map((item) => (
                    <NewsCard
                      key={item.newsId}
                      newsId={item.newsId}
                      newsTitle={item.newsTitle}
                      source={item.source}
                      sourceIconUrl={item.sourceIconUrl}
                      sourceUrl={item.sourceUrl}
                      publishTime={item.publishTime}
                      importance={item.importance}
                      questions={item.questions}
                      clickable
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
        {isLoadingMore && (
          <div className="flex h-10 items-center justify-center text-sm text-[#757575]">
            {t('Loading...')}
          </div>
        )}
        {/* sentinel — 进入视口时自动加载下一页 */}
        {hasMore && !isLoadingMore && setSentinelRef && (
          <div
            ref={setSentinelRef}
            className="flex h-10 items-center justify-center text-sm text-[#757575]"
          >
            {t('Loading...')}
          </div>
        )}
      </div>
    </div>
  );
}
